'use client';

import { c as _c } from "react/compiler-runtime";
import { MotionConfig } from 'framer-motion';
import * as React from 'react';

/**
 * Shared plumbing for the animated line/segmented tab indicators. Both variants
 * use a single shared-layout `motion` element per group that slides to the active
 * Radix trigger, so the layout-id scoping, reduced-motion handling, ref merging,
 * and `data-state` observer live here to keep the two components in sync.
 *
 * Each variant passes `layoutDependency={value}` on its indicator, gating the
 * layout animation on a selection change: the indicator slides between triggers
 * (their values differ) but never animates when the list itself moves on screen
 * (MKT-1557) — layout shifts move it instantly with its trigger.
 */

const LayoutIdContext = React.createContext<string | null>(null);

// `useLayoutEffect` on the client so the active marker is present on the first
// painted frame (the selected tab never renders without its indicator); plain
// `useEffect` on the server to avoid React's SSR warning.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

/**
 * Wrap a tab list so its triggers share one `layoutId` (scoped per group via
 * `useId`) and honour `prefers-reduced-motion` — reduced-motion users get an
 * instant swap instead of the slide.
 */
export function AnimatedTabIndicatorProvider(t0) {
  const $ = _c(5);
  const {
    children
  } = t0;
  const layoutId = React.useId();
  let t1;
  if ($[0] !== children) {
    t1 = <MotionConfig reducedMotion="user">{children}</MotionConfig>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== layoutId || $[3] !== t1) {
    t2 = <LayoutIdContext.Provider value={layoutId}>{t1}</LayoutIdContext.Provider>;
    $[2] = layoutId;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

/**
 * Mirrors a Radix trigger's `data-state` into React state so the shared indicator
 * can mount on the active trigger, and merges the observer ref with a forwarded
 * ref so consumers can still pass their own.
 */
export function useActiveTabIndicator(externalRef: React.Ref<HTMLButtonElement> | undefined) {
  const layoutId = React.useContext(LayoutIdContext);
  const ref = React.useRef<HTMLButtonElement>(null);
  const [isActive, setIsActive] = React.useState(false);
  const mergedRef = React.useCallback((node: HTMLButtonElement | null) => {
    ref.current = node;
    if (typeof externalRef === 'function') {
      externalRef(node);
    } else if (externalRef) {
      externalRef.current = node;
    }
  }, [externalRef]);
  useIsomorphicLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const update = () => setIsActive(element.getAttribute('data-state') === 'active');
    update();
    const observer = new MutationObserver(update);
    observer.observe(element, {
      attributes: true,
      attributeFilter: ['data-state']
    });
    return () => observer.disconnect();
  }, []);
  return {
    layoutId,
    isActive,
    ref: mergedRef
  };
}